// frontend/src/pages/dashboard/ProjectPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import realtime, { pollingManager } from '../../hooks/RealtimePoll';
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { highlightMentions } from "../../utils/sanitizeContent";
import { projectAPI, userAPI, attachmentAPI, clientAPI, commentAPI } from "../../services/api";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import BurndownChart from '../../components/charts/BurndownChart';
import { convertToCompanyTime, convertLocalToUTC, convertToLocalInput } from "../../utils/time";
import { validateFile, ACCEPTED_MIME_TYPES } from '../../utils/boardHelpers';
import {
  Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Alert, Chip, IconButton, Tooltip, List, ListItem, ListItemText,
  Avatar, Badge, Divider, useTheme, Grid, CircularProgress, Switch, useMediaQuery,
  Collapse, Autocomplete, InputAdornment, FormHelperText, Stack, Pagination
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon,
  Dashboard as DashboardIcon, AttachFile as AttachFileIcon, Download as DownloadIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon, Search as SearchIcon, FilterListOff as FilterOffIcon,
  Timeline as TimelineIcon, TrendingDown as TrendingDownIcon, Flag as FlagIcon, Close as CloseIcon,
  Sort as SortIcon, MoreVert as MoreVertIcon, Task as TaskIcon, ViewList as ViewListIcon, Tune
} from "@mui/icons-material";
import { getAvatarUrl } from "../../utils/imageHelpers";
// Import Split Components
import ProjectCard from './ProjectComponents/ProjectCard';
import ProjectCommentSection from './ProjectComponents/ProjectCommentSection';
import ImagePreviewDialog from './ProjectComponents/ImagePreviewDialog';
import ProjectCardSkeleton from './ProjectComponents/ProjectCardSkeleton';
import ProjectTableRowSkeleton from './ProjectComponents/ProjectTableRowSkeleton';
import ProjectTable from './ProjectComponents/ProjectTable';
import ProjectFormDialog from './ProjectComponents/ProjectFormDialog';
import ProjectDetailsDialog from './ProjectComponents/ProjectDetailsDialog';
import ProjectAttachmentsDialog from './ProjectComponents/ProjectAttachmentsDialog';

// ============ MAIN COMPONENT ============
const ProjectPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const { user: authUser, company } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const seenCommentsRef = useRef({});

  // State declarations
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [alertTimeout, setAlertTimeout] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("add");
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [viewingProject, setViewingProject] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [projectComments, setProjectComments] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [newCommentText, setNewCommentText] = useState({});
  const [commentFiles, setCommentFiles] = useState({});
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedProjectAttachments, setSelectedProjectAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectFinancials, setProjectFinancials] = useState(null);
  const [burndownData, setBurndownData] = useState(null);
  const [burndownLoading, setBurndownLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null,
  });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Show 10 projects per page
  const [totalPages, setTotalPages] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  
  const [formData, setFormData] = useState({
    name: "", description: "", start_date: "", end_date: "", status_id: "",
    assigned_user_ids: [], client_id: "", billing_model: "hourly",
    hourly_rate: "", fixed_price: "", currency: "USD", estimated_hours: "", default_sprint_duration_weeks: 2,
  });
  const [formErrors, setFormErrors] = useState({});
  const [filters, setFilters] = useState({ search: "", status: "", client: "", assignee: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", name: "", attachment: null });

  const userMap = useMemo(() => {
    const map = {};
    users.forEach((u) => { map[u.id] = u; });
    return map;
  }, [users]);

  const isAdmin = authUser?.teamMembership?.role?.name === "system administrator";

  const hasPermission = useCallback(
    (perm) => {
      if (!authUser) return false;
      if (authUser.hasPermission && typeof authUser.hasPermission === "function") return authUser.hasPermission(perm);
      const role = authUser.teamMembership?.role;
      if (!role) return false;
      if (role.name?.toLowerCase() === "system administrator") return true;
      const perms = Array.isArray(role.permissions) ? role.permissions.map((p) => p.name) : [];
      return perms.includes(perm);
    },
    [authUser]
  );

  const filteredProjects = useMemo(() => {
    let result = projects.filter(project => {
      const matchesSearch = !filters.search || project.name.toLowerCase().includes(filters.search.toLowerCase()) || project.description?.toLowerCase().includes(filters.search.toLowerCase()) || project.client?.name?.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = !filters.status || project.status?.key === filters.status;
      const matchesClient = !filters.client || String(project.client_id) === String(filters.client);
      const matchesAssignee = !filters.assignee || project.assignedUsers?.some(u => String(u.id) === String(filters.assignee));
      return matchesSearch && matchesStatus && matchesClient && matchesAssignee;
    });

    // Apply sorting only if field and direction are set
    if (sortConfig.field && sortConfig.direction) {
      result = [...result].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.field) {
          case 'name':
            aValue = a.name?.toLowerCase() || '';
            bValue = b.name?.toLowerCase() || '';
            break;
          case 'start_date':
            aValue = a.start_date ? new Date(a.start_date).getTime() : 0;
            bValue = b.start_date ? new Date(b.start_date).getTime() : 0;
            break;
          case 'end_date':
            aValue = a.end_date ? new Date(a.end_date).getTime() : 0;
            bValue = b.end_date ? new Date(b.end_date).getTime() : 0;
            break;
          case 'status':
            aValue = a.statusObj?.name?.toLowerCase() || '';
            bValue = b.statusObj?.name?.toLowerCase() || '';
            break;
          default:
            // Default sort by created_at descending
            aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
            bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort: by created_at descending (newest first)
      result = [...result].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return result;
  }, [projects, filters, sortConfig]);

  // Paginated projects
  const currentProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProjects.slice(startIndex, endIndex);
  }, [filteredProjects, currentPage, itemsPerPage]);

  // Update total pages when filtered projects change
  useEffect(() => {
    const calculatedPages = Math.ceil(filteredProjects.length / itemsPerPage);
    setTotalPages(calculatedPages > 0 ? calculatedPages : 1);
    // Reset to page 1 if current page exceeds total pages
    if (currentPage > calculatedPages && calculatedPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredProjects.length, itemsPerPage, currentPage]);

  const resetFilters = () => setFilters({ search: "", status: "", client: "", assignee: "" });

  const renderFilterChips = () => {
    const chips = [];
    if (filters.search) chips.push(`Search: "${filters.search}"`);
    if (filters.status) chips.push(`Status: ${projectStatuses.find(s => s.key === filters.status)?.name || filters.status}`);
    if (filters.client) chips.push(`Client: ${clients.find(c => String(c.id) === filters.client)?.name || 'Unknown'}`);
    if (filters.assignee) chips.push(`Assignee: ${users.find(u => String(u.id) === filters.assignee)?.name || 'Unknown'}`);
    return chips;
  };

  const formatBillingInfo = (project) => {
    if (!project) return "N/A";
    const currency = project.currency || "USD";
    switch (project.billing_model) {
      case "hourly": return `${project.hourly_rate || "0"} ${currency}/hr`;
      case "fixed": return `${project.fixed_price || "0"} ${currency} (Fixed)`;
      case "monthly": return `${project.fixed_price || "0"} ${currency}/month`;
      case "quarter": return `${project.fixed_price || "0"} ${currency}/quarter`;
      case "half-yearly": return `${project.fixed_price || "0"} ${currency}/6 months`;
      case "yearly": return `${project.fixed_price || "0"} ${currency}/year`;
      case "mixed": return `Mixed (${project.hourly_rate || "0"}/hr + ${project.fixed_price || "0"} fixed)`;
      case "not-applicable": default: return "Not Applicable";
    }
  };

  const formatDate = (dateString, format = "MMM DD") => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const options = { month: 'short', day: '2-digit' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ============ HANDLE PROJECT UPDATES ============
  const handleProjectUpdates = useCallback((updatedProjects) => {
    setProjects((prev) => {
      const existingMap = new Map(prev.map((p) => [p.id, p]));
      const merged = [];
      updatedProjects.forEach((upd) => {
        const isAssigned = (upd.assigned_users || []).some((u) => u.id === authUser?.id);
        if (!isAssigned && !isAdmin) return;
        if (upd.is_deleted) { existingMap.delete(upd.id); return; }
        const enriched = {
          ...upd,
          assignedUsers: (upd.assigned_users || []).map((u) => ({ ...u, role: u.role?.name || "No Role" })),
          attachments: upd.attachments || [],
          statusObj: upd.status || null,
          status_id: upd.status_id,
          taskCount: upd.tasks?.length || 0,
          comment_count: upd.comment_count || 0,
        };
        if (existingMap.has(upd.id)) { merged.push({ ...existingMap.get(upd.id), ...enriched }); existingMap.delete(upd.id); }
        else { merged.push(enriched); }
      });
      existingMap.forEach((p) => merged.push(p));
      return merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
  }, [authUser?.id, isAdmin]);

  // ============ REALTIME POLLING ============
  useEffect(() => {
    realtime.start("projects", {}, handleProjectUpdates, 15000);
    return () => { realtime.stop("projects"); };
  }, [handleProjectUpdates]);

  // ============ TAB VISIBILITY OPTIMIZATION ============
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pollingManager.pause("projects");
        if (expandedProjectId) {
          pollingManager.pause(`comments-project-${expandedProjectId}`);
        }
      } else {
        pollingManager.resume("projects");
        if (expandedProjectId) {
          pollingManager.resume(`comments-project-${expandedProjectId}`);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [expandedProjectId]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) { searchInputRef.current.focus(); }
  }, [searchOpen]);

  // ============ REALTIME LISTENER ============
  useEffect(() => {
    realtime.on("projects", handleProjectUpdates);
    return () => { realtime.off("projects", handleProjectUpdates); };
  }, [handleProjectUpdates]);

  // ============ INITIAL LOAD (With Pagination) ============
  useEffect(() => {
    const loadData = async (page = 1, append = false) => {
      try {
        if (!append) setLoading(true); else setLoadingMore(true);
        const [usersResponse, projectsResponse] = await Promise.all([
          userAPI.getAll(),
          projectAPI.getAll(page),
        ]);
        const activeMembers = usersResponse.data.filter((member) => member.is_active);
        const formattedUsers = activeMembers.map((member) => ({
          id: member.user.id, name: member.user.name, email: member.user.email, role: member.role?.name || "N/A", avatar: member.user.avatar,
        }));
        if (!append) setUsers(formattedUsers);
        const projectsData = projectsResponse.data.data || projectsResponse.data;
        const paginationInfo = projectsResponse.data;
        const enrichedProjects = projectsData.map((p) => ({
          ...p,
          assignedUsers: (p.assigned_users || []).map((u) => ({ ...u, role: u.role?.name || "No Role" })),
          attachments: p.attachments || [], 
          statusObj: p.status || null,
          status_id: p.status_id,
          taskCount: p.tasks?.length || 0,
          comment_count: p.comment_count || 0,
        }));
        if (append) {
          setProjects((prev) => [...prev, ...enrichedProjects]);
        } else {
          setProjects(enrichedProjects);
        }
        if (paginationInfo.current_page >= paginationInfo.last_page) {
          setHasMoreProjects(false);
        } else {
          setHasMoreProjects(true);
        }
        setCurrentPage(page);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    loadData(1, false);
  }, []);

  // ============ LOAD MORE PROJECTS ============
  const loadMoreProjects = async () => {
    if (loadingMore || !hasMoreProjects) return;
    const nextPage = currentPage + 1;
    try {
      setLoadingMore(true);
      const projectsResponse = await projectAPI.getAll(nextPage);
      const projectsData = projectsResponse.data.data || projectsResponse.data;
      const paginationInfo = projectsResponse.data;
      const enrichedProjects = projectsData.map((p) => ({
        ...p,
        assignedUsers: (p.assigned_users || []).map((u) => ({ ...u, role: u.role?.name || "No Role", avatar: u.avatar })),
        attachments: p.attachments || [],
        statusObj: p.status || null,
        status_id: p.status_id,
        taskCount: p.tasks?.length || 0,
        comment_count: p.comment_count || 0,
      }));
      setProjects((prev) => [...prev, ...enrichedProjects]);
      setCurrentPage(nextPage);
      if (paginationInfo.current_page >= paginationInfo.last_page) {
        setHasMoreProjects(false);
      }
    } catch (err) {
      console.error("Failed to load more projects:", err);
      toast.error("Failed to load more projects");
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle Sort
  const handleSort = (field) => {
    setSortConfig(prev => {
      // If clicking the same column
      if (prev.field === field) {
        // Cycle through: null -> 'asc' -> 'desc' -> null
        if (prev.direction === null || prev.direction === undefined) {
          return { field, direction: 'asc' };
        } else if (prev.direction === 'asc') {
          return { field, direction: 'desc' };
        } else {
          // Reset to default (no sorting)
          return { field: null, direction: null };
        }
      }
      // If clicking a different column, start with ascending
      return { field, direction: 'asc' };
    });
  };

  // ============ OTHER LOADERS ============
  useEffect(() => { clientAPI.getAll().then((res) => setClients(res.data)).catch(console.error); }, []);
  
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/statuses?type=project`, { headers: { Authorization: `Bearer ${token}` } });
        setProjectStatuses(res.data);
      } catch (err) { console.error("Failed to load project statuses:", err); }
    };
    fetchStatuses();
  }, []);

  // ============ COMMENTS REALTIME ============
  useEffect(() => {
    if (!expandedProjectId) return;
    if (!seenCommentsRef.current[expandedProjectId]) { seenCommentsRef.current[expandedProjectId] = new Set(); }
    const key = `comments-project-${expandedProjectId}`;
    const handleNewComments = (incoming) => {
      if (!incoming || incoming.length === 0) return;
      const deletes = incoming.filter((c) => c.is_deleted);
      const newOnes = incoming.filter((c) => !c.is_deleted);
      if (deletes.length > 0) {
        setProjectComments((prev) => {
          const updated = { ...prev };
          deletes.forEach((d) => {
            Object.keys(updated).forEach((pid) => { updated[pid] = (updated[pid] || []).filter((c) => c.id !== d.id); });
          });
          return updated;
        });
        setProjects((prev) => prev.map((p) => {
          const removed = deletes.filter((c) => c.project_id === p.id);
          if (removed.length === 0) return p;
          return { ...p, comment_count: Math.max((p.comment_count || 0) - removed.length, 0) };
        }));
      }
      if (newOnes.length > 0) {
        setProjectComments((prev) => {
          const updated = { ...prev };
          newOnes.forEach((c) => {
            const pid = c.project_id;
            const list = updated[pid] || [];
            const exists = list.some((x) => x.id === c.id);
            if (!exists) { updated[pid] = [c, ...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); }
          });
          return updated;
        });
        setProjects((prev) => prev.map((p) => {
          const freshForThis = newOnes.filter((c) => c.project_id === p.id);
          if (freshForThis.length === 0) return p;
          const myNew = freshForThis.filter((c) => c.user_id === authUser?.id).length;
          const othersNew = freshForThis.length - myNew;
          return { ...p, comment_count: (p.comment_count || 0) + othersNew, hasNewComments: p.id !== expandedProjectId };
        }));
        newOnes.forEach((c) => {
          if (c.user_id !== authUser?.id && c.project_id === expandedProjectId) {
            toast.info(`💬 ${c.user?.name}: ${c.content?.slice(0, 100) || "New comment"}`);
          }
        });
      }
    };
    realtime.on(key, handleNewComments);
    realtime.start("comments", { project_id: expandedProjectId }, handleNewComments, 8000);
    return () => {
      realtime.stop(key);
    };
  }, [expandedProjectId, authUser]);

  useEffect(() => {
    if (error) {
      if (alertTimeout) { clearTimeout(alertTimeout); }
      const timeout = setTimeout(() => { setError(""); setAlertTimeout(null); }, 5000);
      setAlertTimeout(timeout);
    }
    return () => { if (alertTimeout) { clearTimeout(alertTimeout); } };
  }, [error]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // ============ UTILS & HANDLERS ============
  const fetchAttachments = async (projectId) => {
    try { const response = await attachmentAPI.listByProject(projectId); setAttachments(response.data); }
    catch (err) { console.error("Failed to load attachments:", err); }
  };

  const loadProjectComments = async (projectId) => {
    if (projectComments[projectId]) return;
    setCommentLoading((prev) => ({ ...prev, [projectId]: true }));
    try { const res = await commentAPI.getByProject(projectId); setProjectComments((prev) => ({ ...prev, [projectId]: res.data })); }
    catch (err) { console.error("Failed to load comments:", err); }
    finally { setCommentLoading((prev) => ({ ...prev, [projectId]: false })); }
  };

  const handleOpenAttachmentsDialog = async (project) => {
    try { const response = await attachmentAPI.listByProject(project.id); setSelectedProjectAttachments(response.data); setAttachmentDialogOpen(true); }
    catch (err) { console.error("Failed to load project attachments:", err); setError("Failed to load attachments"); }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await attachmentAPI.delete(attachmentId);
      setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
      setProjects((prev) => prev.map((p) => {
        if (p.id === currentProject?.id) {
          return { ...p, attachments: p.attachments.filter((att) => att.id !== attachmentId) };
        }
        return p;
      }));
      toast.success("Attachment deleted");
    } catch (err) { console.error("Delete attachment failed:", err); setError("Failed to delete attachment"); }
  };

  const handleImagePreview = async (attachment) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/attachments/${attachment.id}/download`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
      const imageUrl = window.URL.createObjectURL(new Blob([response.data]));
      setSelectedImage({ url: imageUrl, name: attachment.original_name, attachment: attachment });
      setImagePreviewOpen(true);
    } catch (error) { console.error('Failed to load image preview:', error); toast.error('Failed to load image preview'); }
  };

  const handlePreviewDownload = async () => { if (selectedImage.attachment) { await handleDownload(selectedImage.attachment); } };

  const handleOpenAddDialog = () => {
    let assignedUserIds = authUser?.id ? [authUser.id] : [];
    const projectId = searchParams.get("project");
    if (projectId) {
      const project = projects.find((p) => String(p.id) === projectId);
      if (project) { assignedUserIds.push(project.created_by); project.assignedUsers?.forEach((u) => assignedUserIds.push(u.id)); }
    }
    setDialogType("add");
    setFormData({ name: "", description: "", start_date: "", end_date: "", status_id: "", assigned_user_ids: [...new Set(assignedUserIds)], client_id: "", billing_model: "hourly", hourly_rate: "", fixed_price: "", currency: "USD", estimated_hours: "" });
    setFiles([]); setAttachments([]); setFormErrors({}); setOpenDialog(true);
  };

  const handleOpenEditDialog = (project) => {
    if (project.status?.key === "completed" && !isAdmin) { alert("Completed projects cannot be edited."); return; }
    const canUpdateGeneral = hasPermission("project.update");
    const canUpdateStatus = hasPermission("project.update.status");
    const canManageTeam = hasPermission("project.manage.team");
    if (!canUpdateGeneral && !canUpdateStatus && !canManageTeam) { alert("You do not have permission to edit this project."); return; }
    setDialogType("edit"); setCurrentProject(project);
    setFormData({
      name: canUpdateGeneral ? project.name : "", description: canUpdateGeneral ? project.description : "",
      start_date: canUpdateGeneral ? convertToLocalInput(project.start_date, company.timezone) : "",
      end_date: canUpdateGeneral ? convertToLocalInput(project.end_date, company.timezone) : "",
      status_id: canUpdateStatus ? project.status_id : "",
      assigned_user_ids: canManageTeam ? project.assignedUsers?.map((u) => u.id) || [] : [],
      client_id: canUpdateGeneral ? project.client_id || "" : "",
      billing_model: canUpdateGeneral ? project.billing_model || "hourly" : "hourly",
      hourly_rate: canUpdateGeneral ? project.hourly_rate || "" : "",
      fixed_price: canUpdateGeneral ? project.fixed_price || "" : "",
      currency: canUpdateGeneral ? project.currency || "USD" : "USD",
      estimated_hours: canUpdateGeneral ? project.estimated_hours || "" : "",
      default_sprint_duration_weeks: canUpdateGeneral ? (project.default_sprint_duration_weeks || 2) : 2,
    });
    setFiles([]); fetchAttachments(project.id); setFormErrors({}); setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setCurrentProject(null); setFiles([]); setAttachments([]); setFormErrors({}); if (fileInputRef.current) fileInputRef.current.value = ""; };

  const handleFormChange = (e) => { const { name, value } = e.target; setFormData((prev) => ({ ...prev, [name]: value })); setFormErrors((prev) => ({ ...prev, [name]: null })); };

  const handleUserChange = (e) => {
    const newSelection = e.target.value;
    const updatedSelection = [...new Set(newSelection)];
    if (!isAdmin) {
      const creatorId = currentProject?.created_by;
      const currentUserId = authUser?.id;
      if (creatorId && !updatedSelection.includes(creatorId)) { updatedSelection.push(creatorId); }
      if (currentUserId && !updatedSelection.includes(currentUserId)) { updatedSelection.push(currentUserId); }
    }
    setFormData((prev) => ({ ...prev, assigned_user_ids: updatedSelection }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = [];
    const invalidFiles = [];
    files.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push({ name: file.name, reason: validation.reason });
      }
    });
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(f => toast.error(`❌ ${f.name}: ${f.reason}`));
    }
    setFiles(validFiles);
    e.target.value = "";
  };

  const handleDownload = async (attachment) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/attachments/${attachment.id}/download`, { responseType: "blob", headers: { Authorization: `Bearer ${token}` } });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", attachment.original_name); document.body.appendChild(link); link.click(); link.remove();
    } catch (error) { console.error("Download failed:", error); toast.error("Download failed: Please re-login."); }
  };

  const handleDelete = (projectId, status) => {
    if (status?.key === "completed" && !isAdmin) { alert("Only admins can delete completed projects."); return; }
    if (window.confirm("Are you sure you want to delete this project?")) {
      projectAPI.delete(projectId).then(() => {
        setProjects((prev) => prev.filter((p) => p.id !== projectId));
        realtime.advanceLastCheck("projects");
        toast.success(`Project deleted`);
      }).catch(() => setError("Failed to delete project"));
    }
  };

  const handleViewTasks = (projectId) => navigate(`/tasks?project=${projectId}`);
  const handleViewBoard = (projectId) => navigate(`/projects/${projectId}/board`);
  const handleViewSprints = (projectId) => navigate(`/projects/${projectId}/sprints`);
  const handleViewBacklog = (projectId) => navigate(`/projects/${projectId}/backlog`);

  const fetchProjectFinancials = async (projectId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/projects/${projectId}/financials`, { headers: { Authorization: `Bearer ${token}` } });
      setViewingProject(prev => prev ? { ...prev, financials: response.data } : null);
      setProjectFinancials(response.data);
    } catch (error) { console.error('Failed to load project financials:', error); }
  };

  const fetchBurndownData = async (projectId) => {
    if (!isAdmin) return;
    setBurndownLoading(true);
    try { const res = await projectAPI.getBurndown(projectId); setBurndownData(res.data); }
    catch (err) { console.error('Failed to load burndown:', err); }
    finally { setBurndownLoading(false); }
  };

  const handleViewDetails = (project) => {
    setViewingProject(project); fetchAttachments(project.id);
    if (authUser?.teamMembership?.role?.name?.toLowerCase() === 'system administrator') { fetchProjectFinancials(project.id); fetchBurndownData(project.id); }
    setViewDetailsOpen(true);
  };

  const handleCloseViewDetails = () => { setViewDetailsOpen(false); setViewingProject(null); setAttachments([]); setProjectFinancials(null); setBurndownData(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const canUpdateGeneral = hasPermission("project.update");
    const canUpdateStatus = hasPermission("project.update.status");
    const canManageTeam = hasPermission("project.manage.team");
    if (!canUpdateGeneral && !canUpdateStatus && !canManageTeam) {
      setError("You have no permissions to update this project.");
      return;
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      if (canUpdateGeneral) {
        Object.entries(formData).forEach(([key, value]) => {
          if (key === "assigned_user_ids") {
            if (canManageTeam) {
              value.forEach((v) => fd.append(`${key}[]`, v));
            }
          } else if (key === "status_id") {
            if (canUpdateStatus) {
              fd.append(key, value);
            }
          } else if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach((v) => fd.append(`${key}[]`, v));
            } else {
              fd.append(key, value);
            }
          }
        });
      } else {
        if (canUpdateStatus && formData.status_id) {
          fd.append("status_id", formData.status_id);
        }
        if (canManageTeam && formData.assigned_user_ids.length > 0) {
          formData.assigned_user_ids.forEach((v) => fd.append("assigned_user_ids[]", v));
        }
      }
      files.forEach((file) => fd.append("attachments[]", file));
      let response;
      let projectId;
      let baseProjectData;
      if (dialogType === "add") {
        response = await projectAPI.create(fd);
        baseProjectData = response.data;
        projectId = baseProjectData.id;
      } else {
        response = await projectAPI.update(currentProject.id, fd);
        baseProjectData = response.data.project || response.data;
        projectId = baseProjectData.id;
      }
      let finalAttachments = [];
      if (files.length > 0) {
        try {
          const attResponse = await attachmentAPI.listByProject(projectId);
          finalAttachments = attResponse.data;
        } catch (attErr) {
          console.warn("Failed to refresh attachments after upload:", attErr);
          finalAttachments = [];
        }
      } else {
        finalAttachments = dialogType === "edit" ? attachments : (baseProjectData.attachments || []);
      }
      const enrichedData = {
        ...baseProjectData,
        assignedUsers: (baseProjectData.assigned_users || []).map((u) => ({
          ...u,
          role: u.role?.name || "No Role",
        })),
        attachments: finalAttachments,
        statusObj: baseProjectData.status || null,
        status_id: baseProjectData.status_id,
        taskCount: baseProjectData.tasks?.length || 0,
        comment_count: baseProjectData.comment_count || 0,
      };
      if (dialogType === "add") {
        setProjects((prev) => [enrichedData, ...prev]);
        toast.success(`Project "${baseProjectData.name}" created successfully`);
      } else {
        setProjects((prev) =>
          prev.map((p) => (p.id === currentProject.id ? enrichedData : p))
        );
        toast.info(`Project "${baseProjectData.name}" updated`);
      }
      setAttachments(finalAttachments);
      realtime.advanceLastCheck("projects");
      setOpenDialog(false);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const res = err.response?.data;
      console.error("❌ Update error:", err);
      if (err.response?.status === 403 && res?.error) {
        setError(res.error);
      } else if (err.response?.status === 422 && res?.errors) {
        setFormErrors(res.errors);
        return;
      } else {
        setError(res?.error || res?.message || err.message || "Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePostComment = async (projectId) => {
    const rawContent = (newCommentText[projectId] || "").trim();
    if (!rawContent) return;
    const content = rawContent.replace(/@\[([^\]]+)\]\([^)]+\)/g, "@$1");
    const fd = new FormData(); fd.append("content", content);
    const files = commentFiles[projectId];
    if (files) Array.from(files).forEach((f) => fd.append("files[]", f));
    const tempId = `temp-${Date.now()}`;
    const tempComment = { id: tempId, content, user_id: authUser?.id, user: authUser, attachments: [], created_at: new Date().toISOString(), is_local: true };
    setProjectComments((prev) => ({ ...prev, [projectId]: [tempComment, ...(prev[projectId] || [])] }));
    setNewCommentText((prev) => ({ ...prev, [projectId]: "" }));
    setCommentFiles((prev) => ({ ...prev, [projectId]: null }));
    try {
      const res = await commentAPI.createForProject(projectId, fd);
      const realComment = res.data;
      setProjectComments((prev) => { const list = prev[projectId] || []; const updated = list.map((c) => (c.id === tempId ? realComment : c)); return { ...prev, [projectId]: updated }; });
      if (realComment.user_id !== authUser?.id && window.toast) { toast.info(`💬 ${realComment.user?.name}: ${realComment.content?.slice(0, 100)}`); }
    } catch (err) {
      console.error("Comment post failed:", err);
      let errorMsg = "Failed to post comment";
      if (err.response?.status === 422 && err.response?.data?.errors) { const firstError = Object.values(err.response.data.errors)[0]?.[0]; errorMsg = firstError || "Invalid file format or size"; }
      else if (err.response?.data?.error) { errorMsg = err.response.data.error; }
      else if (err.response?.data?.message) { errorMsg = err.response.data.message; }
      toast.error(errorMsg);
      setProjectComments((prev) => ({ ...prev, [projectId]: (prev[projectId] || []).filter((c) => c.id !== tempId) }));
    }
  };

  const handleDeleteComment = async (projectId, commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await commentAPI.delete(commentId);
      setProjectComments((prev) => { const existing = prev[projectId] || []; const updatedComments = existing.filter((c) => c.id !== commentId); if (seenCommentsRef.current[projectId]) { seenCommentsRef.current[projectId].delete(commentId); } return { ...prev, [projectId]: updatedComments }; });
      setProjects((prev) => prev.map((p) => p.id === projectId ? { ...p, comment_count: Math.max((p.comment_count || 0) - 1, 0) } : p));
      const resourceKey = `comments-project-${projectId}`;
      realtime.advanceLastCheck(resourceKey);
    } catch (err) { console.error("Delete failed:", err); toast.error("Failed to delete comment"); }
  };

  const cardHandlers = useMemo(() => ({
    onExpand: (id) => { setExpandedProjectId(id); loadProjectComments(id); realtime.start("comments", { project_id: id }); },
    onCollapse: (id) => { setExpandedProjectId(null); realtime.stop(`comments-project-${id}`); },
    onViewDetails: handleViewDetails, onViewBoard: handleViewBoard,
    onViewSprints: handleViewSprints,
    onViewBacklog: handleViewBacklog,
    onViewTasks: handleViewTasks,
    onEdit: handleOpenEditDialog,
    onDelete: handleDelete, onPostComment: handlePostComment, onDeleteComment: handleDeleteComment,
    expandedProjectId: expandedProjectId
  }), [expandedProjectId, handleViewDetails, handleViewBoard, handleViewSprints, handleViewBacklog, handleViewTasks, handleOpenEditDialog, handleDelete, handlePostComment, handleDeleteComment]);

  // ============ RENDER ============
  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 3, flexDirection: isMobile ? "column" : "row", gap: 1 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={600}>All Projects</Typography>
          </Box>
          {hasPermission("project.create") && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog} sx={{ height: 48, px: 3, borderRadius: 3, fontWeight: 600, letterSpacing: '0.5px', background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, boxShadow: '0 4px 15px rgba(0,0,0,0.15)', '&:hover': { boxShadow: '0 8px 25px rgba(0,0,0,0.25)', transform: 'translateY(-1px)', background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)` }, '&:active': { transform: 'translateY(0)' }, width: isMobile ? "100%" : "auto" }}>
              Add Project
            </Button>
          )}
        </Box>

        {/* Search & Filters */}
        <Paper elevation={0} sx={{ width: '100%', p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 1.5 }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { xs: '100%', sm: 'auto' }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
              {!searchOpen ? (
                <IconButton onClick={() => setSearchOpen(true)} size="medium" sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', width: 40, height: 40, '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main' }, transition: 'all 0.2s' }}><SearchIcon /></IconButton>
              ) : (
                <TextField size="small" inputRef={searchInputRef} placeholder="Search projects..." value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>), endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => filters.search ? setFilters(prev => ({ ...prev, search: '' })) : setSearchOpen(false)} sx={{ p: 0.5 }}><CloseIcon fontSize="small" sx={{ color: 'text.secondary' }} /></IconButton></InputAdornment>), sx: { borderRadius: 2, bgcolor: 'background.paper', '& fieldset': { borderColor: 'divider' }, '&:hover fieldset': { borderColor: 'primary.light' }, '&.Mui-focused fieldset': { borderColor: 'primary.main' } } }} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 } }} />
              )}
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, alignItems: 'center', flexWrap: 'nowrap', ml: { sm: 'auto' } }}>
              <Autocomplete size="small" options={projectStatuses} getOptionLabel={(opt) => opt.name || ''} value={projectStatuses.find(s => s.key === filters.status) || null} onChange={(e, newValue) => setFilters(prev => ({ ...prev, status: newValue?.key || '' }))} renderInput={(params) => (<TextField {...params} placeholder="Status" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper', height: 40, minWidth: 130 } }} />)} sx={{ minWidth: 130 }} />
              <Autocomplete size="small" options={clients} getOptionLabel={(opt) => opt.name || ''} value={clients.find(c => String(c.id) === String(filters.client)) || null} onChange={(e, newValue) => setFilters(prev => ({ ...prev, client: newValue?.id ? String(newValue.id) : '' }))} renderInput={(params) => (<TextField {...params} placeholder="Client" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper', height: 40, minWidth: 140 } }} />)} sx={{ minWidth: 140 }} />
              <Autocomplete size="small" options={users} getOptionLabel={(opt) => opt.name || ''} value={users.find(u => String(u.id) === String(filters.assignee)) || null} onChange={(e, newValue) => setFilters(prev => ({ ...prev, assignee: newValue?.id ? String(newValue.id) : '' }))} renderInput={(params) => (<TextField {...params} placeholder="Assignee" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper', height: 40, minWidth: 140 } }} />)} sx={{ minWidth: 140 }} />
              <Tooltip title="Clear all filters"><IconButton onClick={resetFilters} size="medium" sx={{ bgcolor: (filters.search || filters.status || filters.client || filters.assignee) ? 'primary.light' : 'grey.200', color: (filters.search || filters.status || filters.client || filters.assignee) ? 'primary.contrastText' : 'text.secondary', width: 40, height: 40, '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' }, transition: 'all 0.2s' }}><FilterOffIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: { xs: 'auto', sm: 'auto' } }}>
              <Button size="small" variant="outlined" startIcon={<Tune />} onClick={() => setFiltersOpen(!filtersOpen)} sx={{ borderRadius: 2, height: 40, textTransform: 'none', minWidth: 'auto', px: 2 }}>{filtersOpen ? 'Hide' : 'Filters'}</Button>
            </Box>
          </Box>
          <Collapse in={filtersOpen} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
              <Autocomplete size="small" options={projectStatuses} getOptionLabel={(opt) => opt.name || ''} value={projectStatuses.find(s => s.key === filters.status) || null} onChange={(e, newValue) => setFilters(prev => ({ ...prev, status: newValue?.key || '' }))} renderInput={(params) => <TextField {...params} label="Status" size="small" />} fullWidth />
              <Autocomplete size="small" options={clients} getOptionLabel={(opt) => opt.name || ''} value={clients.find(c => String(c.id) === String(filters.client)) || null} onChange={(e, newValue) => setFilters(prev => ({ ...prev, client: newValue?.id ? String(newValue.id) : '' }))} renderInput={(params) => <TextField {...params} label="Client" size="small" />} fullWidth />
              <Autocomplete size="small" options={users} getOptionLabel={(opt) => opt.name || ''} value={users.find(u => String(u.id) === String(filters.assignee)) || null} onChange={(e, newValue) => setFilters(prev => ({ ...prev, assignee: newValue?.id ? String(newValue.id) : '' }))} renderInput={(params) => <TextField {...params} label="Assignee" size="small" />} fullWidth />
              <Button size="small" variant="contained" onClick={resetFilters} fullWidth sx={{ borderRadius: 2, height: 40 }}>Clear All Filters</Button>
            </Box>
          </Collapse>
        </Paper>

        {renderFilterChips().length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {renderFilterChips().map((chip, i) => (
              <Chip key={i} label={chip} onDelete={() => {
                if (chip.startsWith('Search:')) setFilters(prev => ({ ...prev, search: '' }));
                else if (chip.startsWith('Status:')) setFilters(prev => ({ ...prev, status: '' }));
                else if (chip.startsWith('Client:')) setFilters(prev => ({ ...prev, client: '' }));
                else if (chip.startsWith('Assignee:')) setFilters(prev => ({ ...prev, assignee: '' }));
              }} size="small" />
            ))}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Project List with Skeleton Loader */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          {loading ? (
            isMobile ? (
              Array.from({ length: itemsPerPage }).map((_, index) => (
                <ProjectCardSkeleton key={index} />
              ))
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableBody>
                    <ProjectTableRowSkeleton 
                      isAdmin={isAdmin}
                      isMobile={isMobile}
                      isSmallDesktop={isSmallDesktop}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : currentProjects.length > 0 ? (
            <>
              {isMobile ? (
                currentProjects.map((project, index) => (
                  <ProjectCard 
                    key={project.id} 
                    project={project} 
                    index={(currentPage - 1) * itemsPerPage + index} 
                    handlers={cardHandlers} 
                    authUser={authUser} 
                    hasPermission={hasPermission} 
                    theme={theme} 
                    company={company} 
                    newCommentText={newCommentText} 
                    setNewCommentText={setNewCommentText} 
                    commentFiles={commentFiles} 
                    setCommentFiles={setCommentFiles} 
                    projectComments={projectComments} 
                    commentLoading={commentLoading} 
                    handleDownload={handleDownload} 
                    handleImagePreview={handleImagePreview} 
                  />
                ))
              ) : (
                <TableContainer component={Paper}>
                  <ProjectTable
                    projects={currentProjects}
                    handlers={cardHandlers}
                    authUser={authUser}
                    hasPermission={hasPermission}
                    theme={theme}
                    company={company}
                    newCommentText={newCommentText}
                    setNewCommentText={setNewCommentText}
                    commentFiles={commentFiles}
                    setCommentFiles={setCommentFiles}
                    projectComments={projectComments}
                    commentLoading={commentLoading}
                    handleDownload={handleDownload}
                    handleImagePreview={handleImagePreview}
                    expandedProjectId={expandedProjectId}
                    isAdmin={isAdmin}
                    isMobile={isMobile}
                    isSmallDesktop={isSmallDesktop}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </TableContainer>
              )}
              
              {/* ✅ MATERIAL-UI PAGINATION */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                py: 3,
                gap: 2,
                flexWrap: 'wrap'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Page {currentPage} of {totalPages}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  disabled={loading}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                    },
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {filteredProjects.length} total project{filteredProjects.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="body1" color="text.secondary">
                No projects found
              </Typography>
            </Box>
          )}
        </Box>

        {/* Dialog: Add/Edit Project */}
        <ProjectFormDialog
          open={openDialog}
          onClose={handleCloseDialog}
          dialogType={dialogType}
          formData={formData}
          formErrors={formErrors}
          projects={projects}
          users={users}
          clients={clients}
          projectStatuses={projectStatuses}
          attachments={attachments}
          files={files}
          setFiles={setFiles}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          authUser={authUser}
          hasPermission={hasPermission}
          currentProject={currentProject}
          company={company}
          handleFormChange={handleFormChange}
          handleUserChange={handleUserChange}
          handleFileChange={handleFileChange}
          handleDownload={handleDownload}
          handleDeleteAttachment={handleDeleteAttachment}
          fileInputRef={fileInputRef}
          userMap={userMap}
        />

        {/* View Details Dialog */}
        <ProjectDetailsDialog
          open={viewDetailsOpen}
          onClose={handleCloseViewDetails}
          viewingProject={viewingProject}
          attachments={attachments}
          projectFinancials={projectFinancials}
          burndownData={burndownData}
          burndownLoading={burndownLoading}
          isAdmin={isAdmin}
          company={company}
          handleDownload={handleDownload}
          navigate={navigate}
        />

        {/* Task Attachments Dialog */}
        <ProjectAttachmentsDialog
          open={attachmentDialogOpen}
          onClose={() => setAttachmentDialogOpen(false)}
          selectedProjectAttachments={selectedProjectAttachments}
          handleDownload={handleDownload}
        />

        {/* Image Preview Dialog */}
        <ImagePreviewDialog
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          imageUrl={selectedImage.url}
          imageName={selectedImage.name}
          onDownload={handlePreviewDownload}
        />
      </Box>
    </DashboardLayout>
  );
};

export default ProjectPage;